// Autrona Acoustic Computer Kernel Module: Screen
// Handle all text screen drawing, save/restore, lines management, styles,
// teletype animations and sequences of this UNIX-like terminal emulation.

import * as utils from './utils.js';
import * as main from '../main.js';
import * as keyboard from './keyboard.js';

export {
    init, overflowMode,
    save, restore, clear,
    append, appendHTML, insert, insertHTML,
    insertMultiLine, insertMultiLineHTML, backspace, 
    newLine, clearLine, styleLine, pinCurrentLine, cursor, dots, removeDots,
    replaceLine,
    fillScreenWithEmptyLines,
    teletype, sequence, sequence2, unveil,
    choice, askText
}

// Variables
let screen;
let container;
let saveBuffer;
let unveilBuffer;
let currentLine;
let overflow = 'scroll'; // scroll or clear
let timeout;
let timeoutBis;

const screenCols = 80;
const screenLines = 42;

// Initialize module
function init() {
    screen = document.querySelector('.screen');
    container = document.querySelector('.container');
}

function overflowMode(mode) {
    overflow = mode;
}

function save() {
    saveBuffer = screen.innerHTML;
}

function restore() {
    clearTimeout(timeout);
    clearTimeout(timeoutBis);
    screen.innerHTML = saveBuffer;
    currentLine = screen.querySelector('p:last-of-type');
}

function clear(includePinned = true) {
    if (includePinned) {
        screen.innerHTML = '';
        currentLine = null;
        clearTimeout(timeout);
        clearTimeout(timeoutBis);
    } else {
        screen.querySelectorAll('p:not(.pinned)').forEach(p => p.remove());
    }
}

function append(content, ...style) {
    if (style.length) styleLine(...style);
    currentLine.innerText += content;
    if (DEVMODE) checkLineLength(currentLine.innerText);
    scroll();
}

function appendHTML(content, ...style) {
    if (style.length) styleLine(...style);
    currentLine.innerHTML += content;
    if (DEVMODE) checkLineLength(currentLine.innerText);
    scroll();
}

function scroll() {
    // Scroll to bottom if necessary
    if (container.scrollTop !== container.scrollHeight) {
        setTimeout(() => {
            container.scrollTop = container.scrollHeight;
        }, 0);
    }
}

function insert(content, ...style) {
    newLine();
    append(content, ...style);
}

function insertHTML(content, ...style) {
    newLine();
    appendHTML(content, ...style);
}

function insertMultiLine(content, ...style) {
    content.split('\n').forEach(c => insert(c, ...style));
}

function insertMultiLineHTML(content, ...style) {
    content.split('\n').forEach(c => insertHTML(c, ...style));
}

function checkLineLength(content) {
    if (content.length > screenCols) console.warn('Warning: Content is larger than screen columns: ' + content.length, content);
}

// Delete one (or more if specified) characters from the screenBuffer
function backspace(n = 1) {
    currentLine.innerText = currentLine.innerText.slice(0, -n);
}

function newLine() {
    // Remove blinking cursor and/or dots if active
    if (currentLine) currentLine.classList.remove('cursor');
    if (currentLine) currentLine.classList.remove('dots');

    // Limit screen rows to screenLines
    if (screen.querySelectorAll('p').length >= screenLines) {
        if (overflow === 'clear') {
            clear(false);
        } else { // scroll
            // Remove the first paragraph which is not pinned
            screen.querySelector('p:not(.pinned)').remove();
        }
    }

    // Create a new paragraph
    const p = document.createElement('p');
    screen.appendChild(p);
    currentLine = p;
}

function clearLine(n = 1) {
    for (let i = 0; i < n; i++) {
        currentLine.remove();
        currentLine = screen.querySelector('p:last-of-type');
    }
}

function styleLine(...style) {
    currentLine.classList.add(...style);
}

function pinCurrentLine() {
    currentLine.classList.add('pinned');
}

function cursor() {
    currentLine.classList.add('cursor');
}

function dots() {
    // add three bouncing animated dots
    currentLine.classList.add('dots');
}

function removeDots() {
    if (currentLine) currentLine.classList.remove('dots');
}

function replaceLine(content, n = null) {
    if (n == null) n = screen.querySelectorAll('p').length;
    screen.querySelector(`p:nth-child(${n})`).innerHTML = content;
}

function fillScreenWithEmptyLines() {
    const linesToFill = screenLines - screen.querySelectorAll('p').length;
    for (let i = 0; i < linesToFill; i++) {
        newLine();
    }
}

// Automated teletype
// Will append `string` to the current line one character at a time,
// with `delay` milliseconds between each character.
function teletype(string, delay = 33, callback = null) {
    if (string.length > 0) {    
        let charDelay = delay;
        if (delay === '00' || delay == 'human') { // TODO: see if we can remove the '00' test
            charDelay = Math.floor(Math.random() * 8) ** 2 + 50;
        }

        // Handle inline delays
        let inlineDelayMatch = string.match(/^(§([0-9]+)).*$/);
        if (inlineDelayMatch) {
            charDelay = inlineDelayMatch[2];
            string = string.replace(inlineDelayMatch[1], '').trim();
        }

        // Remove delays for development
        if (DEVMODE && keyboard.activeModifiers.includes('Control')) charDelay = 16;

        timeout = setTimeout(() => {
            if (string.slice(0, 1) === '§') {
                backspace();
            } else {
                append(string.slice(0, 1));
            }
            string = string.slice(1);
            teletype(string, delay, callback);
        }, charDelay);
    } else {
        if (typeof callback == 'function') callback();
    }
}

// Screen sequence
// A sequence of steps, each can be of different types
//  `Any text`         (string).     Will append the text to the current line.
//  `!n`               (new line).   Will open a new line.
//  `!c`               (clear).      Will clear the screen
//  `!s:my-class`      (CSS class).  Will add 'my-class' to the current line.
//  `!t:anytext:delay` (teletype).   Will teletype a string with given delay between each character
//  `!H:anytext      ` (HTML).       Will append the HTML to the current line.
//  `500`              (number).     Will delay further steps by X milliseconds.
function sequence(seq, callback) {
    if (seq.length > 0) {
        const step = seq[0];
        if (typeof step == 'string') {
            let matchNewLine = step.match(/^!n$/);
            let matchClear = step.match(/^!c$/);
            let matchStyle = step.match(/^!s:([a-z0-9\-]+)$/);
            let matchTeletype = step.match(/^!t:(.+):([0-9]+)$/);
            let matchHTML = step.match(/^!h:(.+)$/);

            switch (true) {
                case matchNewLine != null:
                    newLine();
                    break;
                case matchClear != null:
                    clear();
                    break;
                case matchStyle != null:
                    styleLine(matchStyle[1]);
                    break;
                case matchTeletype != null:
                    teletype(matchTeletype[1], matchTeletype[2], () => {
                        // When teletype finished, recursively step ahead in sequence
                        sequence(seq.slice(1), callback);
                    });
                    break;
                case matchHTML != null:
                    appendHTML(matchHTML[1]);
                    break;
                default:
                    append(step);
            }
            // Recursively step ahead in sequence
            if (!matchTeletype) sequence(seq.slice(1), callback);
        } else if (typeof step == 'number') {
            // Recursively step ahead in sequence with a milliseconds delay
            timeout = setTimeout(() => {
                sequence(seq.slice(1), callback);
            }, step);
        }
    } else {
        callback();
    }
}

// New screen sequence
// A sequence of multi-line text which is to be rendered on screen
// Default mode is teletype, each caracter appended to screen one at a time
// with human-like timing.
// Handles a post-lead line delay token in the sequence: '§2000' for a 2s delay
// TODO: BBCode-like markup: (Should be implemented in teletype fonction ?)
//   - [s:col-green]...[/s] Styling with spans
function sequence2(seq, opts, callback = null) {
    const options = {
        /* Default options */
        mode: 'teletype',
        color: 'col-orange',
        lead: '',
        lineDelay: 1000,
        dotsDelay: 2000,
        endDelay: 1000,
        newLineAtStart: true,
        newLineAtEnd: true,
        teletypeDelay: 'human',
    };

    // Overwrite any default option with `opts` parameter
    utils.mergeExistingKeys(opts, options);

    if (seq.length > 0) {
        if (options.newLineAtStart) {
            newLine();
            options.newLineAtStart = false; // To cancel new line from recursive use
        }

        // Get sequence lines as an array
        let lines = seq.split('\n');

        // Get the first line
        let line = lines[0];

        // Remove any white-space around that line (code indents mostly)
        line = line.trim();

        openLine();

        // Find optional an line delay at the string's start (`§500` for a 500ms delay)
        let lineDelay = options.lineDelay;
        let lineDelayMatch = line.match(/^(§([0-9]+)).*$/);
        if (lineDelayMatch) {
            lineDelay = lineDelayMatch[2];
            line = line.replace(lineDelayMatch[1], '').trim();
        }

        // Remove delays for development
        if (DEVMODE && keyboard.activeModifiers.includes('Control')) lineDelay = 0;

        // After the first lead occurence, replace all characters with spaces
        options.lead = ''.padStart(options.lead.length);
            
        // Prepare remaining sequence (this line excluded) for recursive use
        const remainingSeq = lines.slice(1).join('\n');

        // For teletype, display dots after 2 seconds if the line delay is longer than 2 seconds
        if (options.mode === 'teletype' && lineDelay > options.dotsDelay) {
            timeoutBis = setTimeout(dots, options.dotsDelay);
        }

        timeout = setTimeout(() => {
            // Default mode: teletype
            switch (options.mode) {
                case 'line':
                    removeDots();
                    append(line);
                    sequence2(remainingSeq, options, callback);
                    break;
                case 'teletype':
                    removeDots();
                    const lines = utils.getLines(line, 80 - options.lead.length)
                    teletypeMultiline(lines.shift());

                    function teletypeMultiline(line) {
                        teletype(line, options.teletypeDelay, () => {
                            // Handling multiline teletype
                            if (lines.length) {
                                openLine();
                                teletypeMultiline(lines.shift());
                            } else {
                                // Recursively step ahead in sequence
                                sequence2(remainingSeq, options, callback);
                            }
                        });
                    }
                    break;
                case 'message':
                    const charDelay = 100; // How much time per char before printing this line
                    dots();
                    timeout = setTimeout(() => {
                        removeDots();
                        teletype(line, options.teletypeDelay, () => {
                            // Recursively step ahead in sequence
                            sequence2(remainingSeq, options, callback);
                        });
                    }, line.length * charDelay);
                    break;
            }
        }, lineDelay);
    } else {
        timeout = setTimeout(() => {
            if (options.newLineAtEnd) newLine();
            if (callback) callback();
        }, options.endDelay);
    }

    function openLine() {
        // Add lead
        insert(options.lead)

        // Style line
        if (typeof options.color === 'string') {
            styleLine(options.color)
        } else {
            styleLine(...options.color)
        };
    }
}

function unveil(callback) {
    // Analogue to save() but need its own buffer to keep save buffer intact
    unveilBuffer = screen.innerHTML;
    clear();
    const paragraphs = unveilBuffer.split('p><p');
    paragraphs.map((p, i) => {
        if (i === 0) {
            paragraphs[i] += 'p>';
        } else if (i === paragraphs.length - 1) {
            paragraphs[i] = '<p' + paragraphs[i];
        } else {
            paragraphs[i] = '<p' + paragraphs[i] + 'p>';
        }
    });

    let i = 0;
    function step() {
        screen.innerHTML += paragraphs[i]
        currentLine = screen.querySelector('p:last-of-type');
        if (i < paragraphs.length - 1) {
            timeout = setTimeout(step, LSI);
        } else {
            if (typeof callback === 'function') callback();
        }
        i++;
    }
    step();
}

// Display a choice list with optional title to be answered with a key press
// `answers` param is an object: { keyName: [answerText, callback]}
// Example:
//  screen.choice('Votre réponse:', {
//      '1': ['Ben.. Je sais pas trop...', passageB],
//      '2': ['Heu... Je vois rien...', passageC],
//  });
// Would yield:
//  Votre réponse:
//    [1] Ben.. Je sais pas trop...
//    [2] Heu... Je vois rien...
//  > 
function choice(title, answers) {
    // Display optional title
    if (title) insert(title);

    let seq = '';

    // Display one choice per line
    for (const [key, answer] of Object.entries(answers)) {
        const indent = 5 + key.length;
        const maxLength = 80 - indent;
    
        const lines = utils.getLines(answer[0], maxLength)

        lines.forEach(((line, index) => {
            if (index === 0) {
                seq += `[${key}] ${line}\n`;
            } else {
                seq += ''.padStart(indent) + line + '\n';
            }
        }));

    };

    sequence2(seq.trim(), {
        color: 'col-dark-cyan',
        lead: '  ',
        lineDelay: 100,
        endDelay: 100,
        mode: 'line',
        newLineAtStart: false,
        newLineAtEnd: false,
    }, () => {
        // Display prompt
        insert('> ', 'col-cyan');
        cursor();
        main.soundEffect('question');
    });

    registerKeyDownEvent();

    // Local one-time event listener
    function registerKeyDownEvent() {
        document.addEventListener('keydown', (e) => {
            const answer = Object.entries(answers).find(answer => answer[0] === e.key);
            if (answer) {
                append(answer[1][0]);
                main.soundEffect('validate');
                answer[1][1]();
            } else {
                console.log('Invalid answer given:', e.key);
                registerKeyDownEvent();
            }
        }, { once: true });
    }
}

function askText(title, callback) {
    // Display optional title
    if (title) insert(title);

    // Display prompt
    insert('> ', 'col-cyan');
    cursor();
    main.soundEffect('question');

    registerKeyDownEvent();

    let answer = '';

    // Local one-time event listener
    function registerKeyDownEvent() {
        document.addEventListener('keydown', (e) => {
            if (e.key == 'Enter') {
                main.soundEffect('validate');
                callback(answer);
            } else if (e.key == 'Backspace') {
                if (answer.length) {
                    // Remove last keystroke from command line on screen
                    backspace();
                    // Remove last keystroke from command line
                    answer = answer.slice(0, -1);
                }
                registerKeyDownEvent();
            } else {
                if (e.key.length == 1) {
                    // Add keystroke to answer and screen buffer
                    answer += e.key;
                    append(e.key);
                }

                // Enable next keystroke
                registerKeyDownEvent();
            }
        }, { once: true });
    }
}
